import {
	Autocomplete,
	CircularProgress,
	InputAdornment,
	Skeleton,
	Stack,
	TextField,
	Typography,
	useTheme,
} from '@mui/material';
import Fuse from 'fuse.js';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useListInsightFieldsQuery } from '@/features/insightFields';
import { useSelector } from 'react-redux';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { ResponsiveAutocomplete } from '@/components/selects/ResponsiveAutocomplete';

const FUSE_OPTIONS = {
	keys: ['name'],
	threshold: 0.2,
};

/**
 * `multiple`: `value` is an array of insightIds and onChange gets the chosen insightIds.
 * Otherwise `value` is an id, docId, insightId or field object and onChange gets the field.
 * `showAllOption` (multiple only): an "All" option that selects every field.
 */
const InsightFieldSelect = ({
	value,
	multiple = false,
	disabled,
	onChange,
	fieldType,
	minimal = false,
	loading = false,
	onBlur = (e) => {},
	error = false,
	canSearch = false,
	clearable = false,
	showAllOption = false,
	isOptionDisabled = (option) => false,
	autofillIfNull = false,
	...props
}) => {
	const { sx, id, name, ...otherProps } = props ?? {};

	const theme = useTheme();

	const minimalInputTheme = theme.components.MuiInputBase.styleOverrides.minimal;

	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;

	const { data: userDetails } = useGetUserDetailsQuery(
		{ email },
		{
			skip: !email,
		},
	);

	const { data: options, ...insightFieldsQuery } = useListInsightFieldsQuery(
		{ fieldType, brokerGroupId: userDetails?.brokerGroupId },
		{
			skip: !fieldType || !userDetails?.brokerGroupId,
			selectFromResult: ({ data, ...rest }) => {
				const fields = data ?? [];
				return {
					...rest,
					data: fields,
				};
			},
		},
	);

	const searchFuse = useMemo(() => {
		const fuseIndex = Fuse.createIndex(FUSE_OPTIONS.keys, options);
		return new Fuse(options, FUSE_OPTIONS, fuseIndex);
	}, [options]);

	const allowSearch = useMemo(() => canSearch || options.length > 15, [options, canSearch]);

	const findMatchingOption = useCallback(
		(v) => {
			if (!value) return null;
			if (typeof value === 'object') {
				return (
					(value.id != null && value.id === v?.id) ||
					(value.docId != null && value.docId === v?.docId) ||
					(value.insightId != null && value.insightId === v?.insightId)
				);
			}
			return value === v?.id || value === v?.docId || value === v?.insightId;
		},
		[value],
	);

	const selectedValue = options.find(findMatchingOption);

	useEffect(() => {
		if (
			!insightFieldsQuery.isLoading &&
			autofillIfNull &&
			options.length > 0 &&
			selectedValue?.insightId == null
		) {
			onChange(options[0]);
		}
	}, [options, autofillIfNull, insightFieldsQuery.isLoading]);

	const isLoading = loading || insightFieldsQuery.isLoading;

	if (multiple) {
		return (
			<ResponsiveAutocomplete
				options={options.map((option) => ({ id: option.insightId, name: option.name }))}
				value={value}
				onChange={onChange}
				multiple
				disabled={disabled || isLoading || options.length === 0}
				loading={isLoading}
				error={error}
				minimal={minimal}
				disableClearable={!clearable}
				showCheckbox
				showClearOption
				showAllOption={showAllOption}
				// Not clearable: the last chosen option can't be removed
				isOptionFixed={(option) => !clearable && value.length === 1 && value.includes(option.id)}
				renderOptionContent={(option) => (
					<Stack
						direction='row'
						sx={{ width: '100%', justifyContent: 'space-between', alignItems: 'center' }}
					>
						<Typography variant='inherit'>{option.name}</Typography>
						<Typography variant='task_updated'>{option.id}</Typography>
					</Stack>
				)}
				getChipProps={(option) => ({ label: `${option.name} (${option.id})` })}
				searchable={allowSearch}
				sx={sx}
			/>
		);
	}

	const isOptionEqualToValue = (option, val) =>
		['id', 'docId', 'insightId'].some(
			(key) => option?.[key] != null && val?.[key] != null && option[key] === val[key],
		);

	return (
		<Autocomplete
			fullWidth
			disabled={disabled || isLoading || options.length === 0}
			options={options}
			loading={isLoading}
			loadingText={<Skeleton variant='text' width='50%' />}
			value={selectedValue ?? null}
			isOptionEqualToValue={isOptionEqualToValue}
			onBlur={onBlur}
			getOptionLabel={(option) => option?.name ?? ''}
			getOptionDisabled={(option) => isOptionDisabled(option)}
			filterOptions={(options, params) => {
				const search = params.inputValue;
				if (options.length > 0 && search && allowSearch) {
					return searchFuse.search(search).flatMap((v) => v?.item);
				}
				return options;
			}}
			disableClearable={!clearable}
			onChange={(e, option) => {
				onChange(option);
				onBlur(e);
			}}
			renderOption={(props, option) => (
				<li {...props} key={`${option.insightId}`} style={{ width: '100%' }}>
					<Stack
						direction='row'
						sx={{
							width: '100%',
							justifyContent: 'space-between',
							alignItems: 'center',
						}}
					>
						<Typography variant='inherit'>{option.name}</Typography>
						<Typography variant='task_updated'>{option.insightId}</Typography>
					</Stack>
				</li>
			)}
			renderInput={(params) => {
				return (
					<TextField
						{...params}
						{...{ error }}
						variant={minimal ? 'standard' : 'outlined'}
						{...(!allowSearch && { onKeyDown: (e) => e.preventDefault() })}
						sx={{
							...(minimal && minimalInputTheme),
							'& .MuiInput-input': { color: theme.palette.primary.main },
							...(!allowSearch && { cursor: 'pointer' }),
						}}
						InputProps={{
							...params.InputProps,
							onBlur,
							...(!allowSearch && {
								readOnly: true,
								style: { caretColor: 'transparent', cursor: 'pointer' },
							}),
							...(isLoading && {
								endAdornment: (
									<InputAdornment position='end'>
										<CircularProgress size={20} />
									</InputAdornment>
								),
							}),
						}}
					/>
				);
			}}
			{...(otherProps ?? {})}
			{...((id || name) && { id: id ?? name, name: name ?? id })}
			sx={{
				...(!allowSearch && {
					cursor: 'pointer',
					'& .MuiInputBase-root': { cursor: 'pointer' },
					'& input': { cursor: 'pointer' },
				}),

				...(minimal && {
					...minimalInputTheme,
					'& .MuiInputBase-root': { backgroundColor: 'transparent' },
				}),
				...(sx ?? {}),
			}}
		/>
	);
};
InsightFieldSelect.displayName = 'InsightFieldSelect';

export default InsightFieldSelect;
